import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangleIcon, ClipboardCheckIcon, InfoIcon, BookOpenIcon, CheckCircleIcon, LockIcon } from 'lucide-react';
import { SectionData } from '../data/module1';
import { MeasureOverlay } from './MeasureOverlay';
import { SectionQuiz } from './SectionQuiz';
interface SectionCardProps {
  section: SectionData;
  onGuidesCompletionChange?: (allCompleted: boolean) => void;
}
export function SectionCard({
  section,
  onGuidesCompletionChange
}: SectionCardProps) {
  const [activeMeasureGuide, setActiveMeasureGuide] = useState<number | null>(null);
  const [completedGuides, setCompletedGuides] = useState<Set<number>>(new Set());
  const [quizCompleted, setQuizCompleted] = useState(false);
  const guidesForSection = section.guides || [];
  const measuresWithGuides = guidesForSection.map(g => g.measureIndex);
  const hasQuiz = section.quiz && section.quiz.length > 0;
  useEffect(() => {
    if (onGuidesCompletionChange) {
      const allGuidesDone = guidesForSection.length === 0 || guidesForSection.every(g => completedGuides.has(g.measureIndex));
      const isQuizDone = !hasQuiz || quizCompleted;
      onGuidesCompletionChange(allGuidesDone && isQuizDone);
    }
  }, [completedGuides, guidesForSection.length, quizCompleted, hasQuiz]);
  const handleGuideComplete = (measureIndex: number) => {
    setCompletedGuides(prev => {
      const next = new Set(prev);
      next.add(measureIndex);
      return next;
    });
    setActiveMeasureGuide(null);
  };
  const handleOpenGuide = (measureIndex: number) => {
    setActiveMeasureGuide(measureIndex);
  };
  const handleCloseGuide = () => {
    setActiveMeasureGuide(null);
  };
  const activeGuide = activeMeasureGuide !== null ? guidesForSection.find(g => g.measureIndex === activeMeasureGuide) : null;
  const activeMeasureText = activeMeasureGuide !== null && section.measures ? section.measures[activeMeasureGuide] || '' : '';
  return <>
      {/* Fullscreen Measure Overlay */}
      <AnimatePresence data-id="element-205">
        {activeMeasureGuide !== null && activeGuide && <MeasureOverlay guide={activeGuide} measureNumber={activeMeasureGuide + 1} measureText={activeMeasureText} onComplete={() => handleGuideComplete(activeMeasureGuide)} onClose={handleCloseGuide} data-id="element-206" />}
      </AnimatePresence>

      <motion.div key={section.id} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="w-full max-w-none mx-auto" data-id="element-207">
        <div className="mb-4" data-id="element-208">
          <h2 className="text-xl md:text-2xl font-bold text-[#1a1a2e] mb-2" data-id="element-209">
            {section.title}
          </h2>
          <div className="w-12 h-1 bg-[#8DC63F] rounded-full" data-id="element-210"></div>
        </div>

        {/* General Content */}
        {section.content && section.content.length > 0 && <div className="mb-4" data-id="element-211">
            <div className="flex items-center mb-2" data-id="element-212">
              <div className="bg-blue-100 p-1.5 rounded-lg mr-2" data-id="element-213">
                <InfoIcon className="h-4 w-4 text-blue-600" data-id="element-214" />
              </div>
              <h3 className="text-lg font-bold text-[#1a1a2e]" data-id="element-215">Übersicht</h3>
            </div>
            <ul className="space-y-1.5 pl-8" data-id="element-216">
              {section.content.map((item, idx) => <li key={idx} className="text-[#4a4a5a] text-base list-disc" data-id="element-217">
                  {item}
                </li>)}
            </ul>
          </div>}

        {/* Two-column grid for Hazards and Measures */}
        <div className="space-y-4" data-id="element-218">
          {/* Hazards */}
          {section.hazards && section.hazards.length > 0 && <div className="bg-orange-50/80 rounded-xl p-4 md:p-5 border border-orange-100 shadow-sm h-fit" data-id="element-219">
              <div className="flex items-center mb-3" data-id="element-220">
                <div className="bg-orange-100 p-1.5 rounded-lg mr-2" data-id="element-221">
                  <AlertTriangleIcon className="h-4 w-4 text-orange-600" data-id="element-222" />
                </div>
                <h3 className="text-lg font-bold text-orange-900" data-id="element-223">
                  Gefährdungen
                </h3>
              </div>
              <ul className="space-y-1.5 pl-8" data-id="element-224">
                {section.hazards.map((hazard, idx) => <li key={idx} className="text-orange-800 text-base list-disc" data-id="element-225">
                    {hazard}
                  </li>)}
              </ul>
            </div>}

          {/* Measures */}
          {section.measures && section.measures.length > 0 && <div className="bg-green-50/50 rounded-xl p-4 md:p-5 border border-green-100 shadow-sm h-fit" data-id="element-226">
              <div className="flex items-center mb-4" data-id="element-227">
                <div className="bg-green-100 p-1.5 rounded-lg mr-2" data-id="element-228">
                  <ClipboardCheckIcon className="h-4 w-4 text-[#8DC63F]" data-id="element-229" />
                </div>
                <h3 className="text-lg font-bold text-green-900" data-id="element-230">
                  Schutzmaßnahmen
                </h3>
                {guidesForSection.length > 0 && <span className="ml-auto text-[10px] md:text-xs font-semibold text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200" data-id="element-231">
                    {completedGuides.size} / {guidesForSection.length}
                  </span>}
              </div>

              <ul className="space-y-2 pl-1 md:pl-2" data-id="element-232">
                {section.measures.map((measure, idx) => {
              const hasGuide = measuresWithGuides.includes(idx);
              const guideCompleted = completedGuides.has(idx);
              return <li key={idx} className="list-none" data-id="element-233">
                      <div className="flex items-start" data-id="element-234">
                        <span className={`mt-1.5 mr-3 w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors ${guideCompleted ? 'bg-[#8DC63F]' : 'bg-gray-300'}`} data-id="element-235" />
                        <div className="flex-1" data-id="element-236">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2" data-id="element-237">
                            <span className={`text-base font-medium transition-colors ${guideCompleted ? 'text-green-900' : 'text-green-800'}`} data-id="element-238">
                              {measure}
                            </span>
                            {hasGuide && <button onClick={() => handleOpenGuide(idx)} className={`flex-shrink-0 flex items-center space-x-1 px-2 py-1 rounded text-xs font-semibold transition-all w-fit ${guideCompleted ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-[#8DC63F] text-white hover:bg-[#7ab036] shadow-sm'}`} data-id="element-239">
                                {guideCompleted ? <>
                                    <CheckCircleIcon className="w-3 h-3" data-id="element-240" />
                                    <span data-id="element-241">Ansehen</span>
                                  </> : <>
                                    <BookOpenIcon className="w-3 h-3" data-id="element-242" />
                                    <span data-id="element-243">Starten</span>
                                  </>}
                              </button>}
                          </div>
                        </div>
                      </div>
                    </li>;
            })}
              </ul>
            </div>}

          {/* Quiz Section */}
          {section.quiz && section.quiz.length > 0 && <div className="mt-8" data-id="element-244">
              <SectionQuiz questions={section.quiz} onComplete={() => setQuizCompleted(true)} data-id="element-245" />
            </div>}
        </div>
      </motion.div>
    </>;
}